///
/// EarthBrowser - Earthquake Browser
///
/// Modern movable window for browsing earthquakes using local OGC sync data
/// Created by Matt Giger on December 19, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { useCallback, useMemo, useState, useEffect, memo, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { sendDatasetAction } from '@/redux/earthBrowserSlice'
import { DatasetBrowserWindow } from '@/components/windows/DatasetBrowserWindow'
import { DatasetVisibilityToggle } from '@/components/windows/DatasetVisibilityToggle'
import { DataBrowserView, type DataBrowserViewHandle } from '@/components/data-browser/DataBrowserView'
import { useRegisterWindowContent } from '@/components/windows/WindowContentRegistry'
import { DataProviderRegistry } from '@/data-providers/DataProviderRegistry'
import { EarthquakeDataProvider } from '@/data-providers/EarthquakeDataProvider.tsx'
import { EarthquakesTable, type EarthquakeProperties } from '@/earthbrowser/dataset-distribution/earthquakes/EarthquakesTable'
import { useDatasetLayerRefresh } from '@/hooks/useDatasetLayerRefresh'
import type { DataRow } from '@/data-providers/types'
import { Activity } from 'lucide-react'
import { isMobile } from '@/earthbrowser/utils/platform'
import { closeDatasetWindow, type DatasetBrowserWindowState } from '@/redux/dialogSlice'

interface EarthquakeBrowserRendererProps {
	windowId: string
	windowState: DatasetBrowserWindowState
}

export const EarthquakeBrowserRenderer = memo(function EarthquakeBrowserRenderer({
	windowId,
	windowState,
}: EarthquakeBrowserRendererProps) {
	const dispatch = useAppDispatch()
	const visibleDatasets = useAppSelector(state => state.earthbrowser.visibleDatasets)
	const earthquakesVisible = visibleDatasets.includes("earthquake")

	// Get singleton scene manager from Redux
	const layerManager = useAppSelector(state => state.datasetScene.layerManager)

	// Local state
	const [isInitialized, setIsInitialized] = useState(false)
	const [isLoading, setIsLoading] = useState(true)
	const [earthquakes, setEarthquakes] = useState<EarthquakeProperties[]>([])
	const [totalCount, setTotalCount] = useState(0)
	const [timeUpdateTrigger, setTimeUpdateTrigger] = useState(0)
	const dataBrowserViewRef = useRef<DataBrowserViewHandle>(null)

	// Load earthquakes from the local table (mount, sync refresh, manual refresh)
	const loadEarthquakes = useCallback(async (showLoading: boolean) => {
		if (showLoading) setIsLoading(true)
		try {
			const loadedEarthquakes = await EarthquakesTable.getAll()
			setEarthquakes(loadedEarthquakes)
			setTimeUpdateTrigger(prev => prev + 1)

			const count = await EarthquakesTable.getCount()
			setTotalCount(count)
		} catch (error) {
			console.error('Failed to load earthquakes:', error)
		} finally {
			if (showLoading) setIsLoading(false)
		}
	}, [])

	// Load earthquakes on mount
	useEffect(() => {
		void loadEarthquakes(true)
	}, [loadEarthquakes])

	// Reload silently whenever the earthquakes layer refreshes after a sync,
	// so the table doesn't show a stale snapshot from when the window opened
	useDatasetLayerRefresh('earthquakes', () => {
		void loadEarthquakes(false)
	})

	// Initialize and update data provider
	useEffect(() => {
		// Don't do anything while initial loading
		if (isLoading) return

		// Only create provider if not initialized
		if (!isInitialized) {
			// Dispose old provider
			const oldProvider = DataProviderRegistry.get(windowId)
			if (oldProvider) {
				void oldProvider.dispose?.()
				DataProviderRegistry.unregister(windowId)
			}

			// Create new provider with current data
			const provider = new EarthquakeDataProvider({
				id: windowId,
				name: 'Earthquakes',
				queryFn: async () => earthquakes // Initial data
			})

			void provider.initialize().then(() => {
				DataProviderRegistry.register(windowId, provider)
				setIsInitialized(true)
			})
		} else {
			// If already initialized, update the provider's cached data directly
			// This happens when earthquakes data changes (new sync, refetch, etc.)
			const provider = DataProviderRegistry.get(windowId) as EarthquakeDataProvider | undefined
			if (provider) {
				provider.updateCachedData(earthquakes)
			}
		}
	}, [earthquakes, windowId, isLoading, isInitialized])

	// Timer to update relative times every minute
	// Sync updates to occur at the change of the minute (seconds = 0)
	useEffect(() => {
		let interval: NodeJS.Timeout | null = null

		// Calculate milliseconds until next minute boundary
		const now = new Date()
		const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds()

		// Wait until the next minute, then start interval
		const timeout = setTimeout(() => {
			setTimeUpdateTrigger(prev => prev + 1)

			// Now start the regular interval that fires every minute
			interval = setInterval(() => {
				setTimeUpdateTrigger(prev => prev + 1)
			}, 60000)
		}, msUntilNextMinute)

		return () => {
			clearTimeout(timeout)
			if (interval) {
				clearInterval(interval)
			}
		}
	}, [])

	// Sync scene layer visibility with Redux state
	useEffect(() => {
		if (layerManager) {
			layerManager.setLayerVisible('earthquakes', earthquakesVisible)
		}
	}, [layerManager, earthquakesVisible])

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			const provider = DataProviderRegistry.get(windowId)
			if (provider) {
				void provider.dispose?.()
				DataProviderRegistry.unregister(windowId)
			}
		}
	}, [windowId])

	// Handle earthquake row click
	const handleRowClick = useCallback((row: DataRow) => {
		const earthquake = row as unknown as EarthquakeProperties
		const featureId = earthquake.id

		// Ensure earthquakes are visible on the map
		if (!earthquakesVisible) {
			dispatch(sendDatasetAction({
				type: "earthquake",
				action: "enable"
			}))
			if (layerManager) {
				layerManager.setLayerVisible('earthquakes', true)
			}
		}

		// Focus the dataset-distribution scene object (QuakeFeature)
		if (layerManager) {
			layerManager.focusSceneObject('earthquakes', featureId)
		}

		if (isMobile()) {
			dispatch(closeDatasetWindow(windowId))
		}
	}, [layerManager, earthquakesVisible, dispatch, windowId])

	// Handle refresh
	const handleRefresh = useCallback(() => {
		void loadEarthquakes(true)
		if (layerManager) {
			void layerManager.refreshLayer('earthquakes')
		}
	}, [loadEarthquakes, layerManager])

	// Content
	const content = useMemo(() => {
		const provider = DataProviderRegistry.get(windowId)

		// Show data if we have a provider and it's initialized
		// Keep showing data during background refetches (don't flash loading indicator)
		if (provider && isInitialized) {
			return (
				<DataBrowserView
					ref={dataBrowserViewRef}
					key={`earthquake-browser-${earthquakes.length}-${timeUpdateTrigger}`}
					provider={provider}
					onRowClick={handleRowClick}
					onRefresh={handleRefresh}
					totalDatabaseCount={totalCount}
					searchPlaceholder="Search by location, magnitude..."
				/>
			)
		}

		// Loading state - only show when dataset is empty (initial load)
		return (
			<div className="flex flex-col items-center justify-center h-full bg-black/20 backdrop-blur-sm">
				<div className="flex flex-col items-center gap-4 p-8">
					<div className="relative w-16 h-16">
						<div className="absolute inset-0 border-4 border-red-500/20 rounded-full" />
						<div className="absolute inset-0 border-4 border-transparent border-t-red-500 rounded-full animate-spin" />
					</div>
					<p className="text-base font-medium text-white">
						Loading Earthquakes
					</p>
					<p className="text-sm text-white/60">
						Initializing...
					</p>
				</div>
			</div>
		)
	}, [windowId, isInitialized, handleRowClick, handleRefresh, totalCount, earthquakes.length, timeUpdateTrigger])

	// Register content with the window content registry for drawer docking
	const contentRenderer = useCallback(() => content, [content])
	useRegisterWindowContent(windowId, contentRenderer)

	// Handle window being brought to front - focus search input
	const handleBringToFront = useCallback(() => {
		dataBrowserViewRef.current?.focusSearch()
	}, [])

	return (
		<DatasetBrowserWindow
			windowState={windowState}
			icon={<Activity className="w-5 h-5" />}
			iconColor="text-red-400"
			titlebarPrefix={<DatasetVisibilityToggle windowType="earthquake" />}
			onBringToFront={handleBringToFront}
		>
			{content}
		</DatasetBrowserWindow>
	)
})
